/**
 * AdminTalentRegion — iter-136
 * Admin review of talent region status. platform_admin can override.
 * Waitlist CSV export is platform_admin only.
 */
import React, { useEffect, useState } from "react";
import { Globe2, Loader2, Download, ShieldCheck, AlertOctagon, Send } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAdminContext } from "@/pages/admin/AdminShell";

const CHIPS = [
    { key: "all",            label: "All" },
    { key: "unverified",     label: "Unverified" },
    { key: "verified_us",    label: "Verified US" },
    { key: "non_us_flagged", label: "Flagged non-US" },
];

export default function AdminTalentRegion() {
    const ctx = useAdminContext();
    const roles = new Set([ctx?.me?.admin_role, ...(ctx?.me?.admin_roles || [])].filter(Boolean));
    const canWrite = roles.has("platform_admin");

    const [rows, setRows] = useState([]);
    const [counts, setCounts] = useState({});
    const [filter, setFilter] = useState("all");
    const [busy, setBusy] = useState(false);
    const [nudgeDialog, setNudgeDialog] = useState({ open: false, preview: null, sending: false, result: null });

    const load = async () => {
        try {
            const url = filter === "all" ? "/admin/talent-region" : `/admin/talent-region?status=${filter}`;
            const { data } = await api.get(url);
            setRows(data.results || []);
            setCounts(data.counts || {});
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to load");
        }
    };
    useEffect(() => { load(); }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

    const override = async (user_id, region_status) => {
        if (!canWrite) return;
        const reason = window.prompt("Reason for override?") || "";
        if (reason.trim().length < 3) { toast.error("Reason required."); return; }
        setBusy(true);
        try {
            await api.patch(`/admin/talent-region/${user_id}`, { region_status, reason });
            toast.success("Override applied.");
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed");
        } finally { setBusy(false); }
    };

    const downloadCsv = async () => {
        try {
            const resp = await api.get("/admin/talent-region/waitlist.csv", { responseType: "blob" });
            const blob = new Blob([resp.data], { type: "text/csv" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url; a.download = "international_waitlist.csv";
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Download failed");
        }
    };

    const openNudge = async () => {
        setNudgeDialog({ open: true, preview: null, sending: false, result: null });
        try {
            const { data } = await api.post("/admin/talent-region/backfill-nudge", { dry_run: true });
            setNudgeDialog((s) => ({ ...s, preview: data }));
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Preview failed");
            setNudgeDialog({ open: false, preview: null, sending: false, result: null });
        }
    };

    const fireNudge = async () => {
        setNudgeDialog((s) => ({ ...s, sending: true }));
        try {
            const { data } = await api.post("/admin/talent-region/backfill-nudge", { dry_run: false });
            setNudgeDialog((s) => ({ ...s, sending: false, result: data }));
            toast.success(`Sent ${data.sent} email${data.sent === 1 ? "" : "s"}.`);
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Send failed");
            setNudgeDialog((s) => ({ ...s, sending: false }));
        }
    };

    return (
        <div className="min-h-screen bg-background text-foreground" data-testid="admin-talent-region-page">
            <div className="max-w-7xl mx-auto p-6">
                <div className="inline-flex items-center gap-2 text-xs uppercase text-emerald-700 dark:text-emerald-400 font-semibold">
                    <Globe2 size={14} /> Trust &amp; Safety
                </div>
                <h1 className="mt-1 font-heading text-3xl font-semibold">Talent Region Review</h1>

                <div className="mt-4 grid grid-cols-3 gap-3 max-w-xl">
                    <div className="rounded-xl border border-border p-3">
                        <div className="text-xs text-muted-foreground uppercase">Verified US</div>
                        <div className="text-2xl font-heading font-semibold text-emerald-600">{counts.verified_us || 0}</div>
                    </div>
                    <div className="rounded-xl border border-border p-3">
                        <div className="text-xs text-muted-foreground uppercase">Unverified</div>
                        <div className="text-2xl font-heading font-semibold text-slate-600">{counts.unverified || 0}</div>
                    </div>
                    <div className="rounded-xl border border-border p-3">
                        <div className="text-xs text-muted-foreground uppercase">Flagged</div>
                        <div className="text-2xl font-heading font-semibold text-rose-600">{counts.non_us_flagged || 0}</div>
                    </div>
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                    {CHIPS.map((c) => (
                        <button
                            key={c.key}
                            onClick={() => setFilter(c.key)}
                            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
                                filter === c.key
                                    ? "bg-foreground text-background border-foreground"
                                    : "bg-background border-border hover:bg-muted"
                            }`}
                            data-testid={`atr-filter-${c.key}`}
                        >{c.label}</button>
                    ))}
                    {canWrite && (
                        <button onClick={downloadCsv}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs border border-border hover:bg-muted"
                            data-testid="atr-waitlist-csv">
                            <Download size={12} /> International waitlist CSV
                        </button>
                    )}
                    {canWrite && (
                        <button onClick={openNudge}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs bg-sky-600 hover:bg-sky-700 text-white font-semibold"
                            data-testid="atr-backfill-nudge-btn">
                            <Send size={12} /> Send backfill nudge
                        </button>
                    )}
                </div>

                <div className="mt-4 rounded-2xl border border-border overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-muted/40 text-xs uppercase text-muted-foreground">
                            <tr>
                                <th className="text-left p-3">Talent</th>
                                <th className="text-left p-3">Status</th>
                                <th className="text-left p-3">Signup country</th>
                                <th className="text-left p-3">Phone country</th>
                                <th className="text-left p-3">State</th>
                                <th className="text-left p-3">Travel</th>
                                {canWrite && <th className="p-3"></th>}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                            {rows.length === 0 && (
                                <tr><td colSpan={canWrite ? 7 : 6} className="p-8 text-center text-muted-foreground">No talent match this filter.</td></tr>
                            )}
                            {rows.map((r) => (
                                <tr key={r.user_id} data-testid={`atr-row-${r.user_id}`}>
                                    <td className="p-3">
                                        <div className="font-medium">{r.name || "—"}</div>
                                        <div className="text-[11px] text-muted-foreground">{r.email}</div>
                                    </td>
                                    <td className="p-3">
                                        <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${
                                            r.region_status === "verified_us" ? "bg-emerald-100 text-emerald-800"
                                            : r.region_status === "non_us_flagged" ? "bg-rose-100 text-rose-800"
                                            : "bg-slate-100 text-slate-800"
                                        }`}>{r.region_status || "unverified"}</span>
                                    </td>
                                    <td className="p-3">{r.signup_ip_country || "—"}</td>
                                    <td className="p-3">{r.phone_country || "—"}{r.phone_line_type ? ` (${r.phone_line_type})` : ""}</td>
                                    <td className="p-3">{r.us_address_state || "—"}</td>
                                    <td className="p-3">{r.travel_flag ? `${r.travel_flag.country || "?"} · ${r.travel_flag.at?.slice(0,10)}` : "—"}</td>
                                    {canWrite && (
                                        <td className="p-3 whitespace-nowrap">
                                            <button disabled={busy}
                                                onClick={() => override(r.user_id, "verified_us")}
                                                className="text-xs px-2 py-1 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white mr-1"
                                                data-testid={`atr-verify-${r.user_id}`}
                                            ><ShieldCheck size={11} className="inline" /> Verify</button>
                                            <button disabled={busy}
                                                onClick={() => override(r.user_id, "non_us_flagged")}
                                                className="text-xs px-2 py-1 rounded-full bg-rose-600 hover:bg-rose-700 text-white"
                                                data-testid={`atr-flag-${r.user_id}`}
                                            ><AlertOctagon size={11} className="inline" /> Flag</button>
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
            {nudgeDialog.open && (
                <div className="fixed inset-0 z-[95] bg-black/60 flex items-center justify-center p-4" data-testid="atr-nudge-dialog">
                    <div className="w-full max-w-md rounded-2xl border border-border bg-background shadow-2xl p-5">
                        <div className="text-xs uppercase text-sky-700 dark:text-sky-400 font-semibold">Backfill nudge</div>
                        <h3 className="font-heading text-lg font-semibold mt-1">Email existing unverified talent</h3>
                        {!nudgeDialog.preview && !nudgeDialog.result && (
                            <div className="mt-4 flex items-center gap-2 text-sm text-muted-foreground">
                                <Loader2 size={14} className="animate-spin" /> Calculating eligible pool…
                            </div>
                        )}
                        {nudgeDialog.preview && !nudgeDialog.result && (
                            <>
                                <div className="mt-4 rounded-xl border border-border p-4 bg-muted/30">
                                    <div className="text-3xl font-heading font-semibold" data-testid="atr-nudge-preview-count">
                                        {nudgeDialog.preview.would_send}
                                    </div>
                                    <div className="text-xs text-muted-foreground uppercase">emails ready to send</div>
                                    <p className="text-[11px] text-muted-foreground mt-2">
                                        Eligible pool: {nudgeDialog.preview.total_eligible} · Cap per run: {nudgeDialog.preview.cap_per_run} · Active in last {nudgeDialog.preview.lookback_days} days
                                    </p>
                                </div>
                                <p className="text-xs text-muted-foreground mt-3">
                                    Each user gets one email with a link to <code>/professional/dashboard</code>. Already-nudged talent are skipped (idempotent).
                                </p>
                                <div className="mt-4 flex justify-end gap-2">
                                    <button
                                        onClick={() => setNudgeDialog({ open: false, preview: null, sending: false, result: null })}
                                        disabled={nudgeDialog.sending}
                                        className="text-xs px-3 py-2 rounded-full border border-border hover:bg-muted"
                                        data-testid="atr-nudge-cancel"
                                    >Cancel</button>
                                    <button
                                        onClick={fireNudge}
                                        disabled={nudgeDialog.sending || nudgeDialog.preview.would_send === 0}
                                        className="text-xs px-3 py-2 rounded-full bg-sky-600 hover:bg-sky-700 text-white font-semibold inline-flex items-center gap-1 disabled:opacity-40"
                                        data-testid="atr-nudge-fire"
                                    >
                                        {nudgeDialog.sending ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                                        Send {nudgeDialog.preview.would_send} email{nudgeDialog.preview.would_send === 1 ? "" : "s"}
                                    </button>
                                </div>
                            </>
                        )}
                        {nudgeDialog.result && (
                            <>
                                <div className="mt-4 rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-4">
                                    <div className="text-sm font-semibold text-emerald-800 dark:text-emerald-300">Batch sent</div>
                                    <div className="text-xs mt-1">Sent: <strong>{nudgeDialog.result.sent}</strong> · Skipped: <strong>{nudgeDialog.result.skipped}</strong> · Remaining eligible: <strong>{nudgeDialog.result.remaining}</strong></div>
                                    {nudgeDialog.result.remaining > 0 && (
                                        <p className="text-[11px] mt-2 text-muted-foreground">
                                            More talent are eligible than the {nudgeDialog.result.cap_per_run} per-run cap allows. Run again tomorrow to send the next batch.
                                        </p>
                                    )}
                                </div>
                                <div className="mt-4 flex justify-end">
                                    <button
                                        onClick={() => setNudgeDialog({ open: false, preview: null, sending: false, result: null })}
                                        className="text-xs px-3 py-2 rounded-full bg-foreground text-background font-semibold"
                                        data-testid="atr-nudge-close"
                                    >Close</button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
